import { CodigoBarras } from '@/componentes/etiqueta/CodigoBarras';

/**
 * A etiqueta de alocação — 100 × 50 mm, uma por volume.
 *
 * É colada na caixa e bipada pelo coletor na hora de guardar. Quem a lê está
 * de pé, com a caixa na mão, muitas vezes no alto de uma escada — e é isso que
 * decide o layout, não estética.
 *
 * A HIERARQUIA É A DO USO
 * -----------------------
 * 1. **O código de barras** ocupa a faixa central inteira. É a única coisa que
 *    a etiqueta precisa fazer bem; todo o resto é conferência humana.
 * 2. **A quantidade**, a maior tipografia da etiqueta. É o que se confere
 *    contra a caixa antes de bipar — errar aqui é alocar 3 unidades como se
 *    fossem 30.
 * 3. **A sequência** (`7 / 60`), para achar a etiqueta certa no maço quando
 *    uma cai ou vem fora de ordem. Por isso é grande, e não uma nota de rodapé.
 * 4. A descrição, em duas linhas, que confirma que a etiqueta é daquela caixa.
 *
 * Code 128, e não EAN-13: o código tem 13 dígitos e parece um EAN, mas o
 * dígito verificador não fecha (conferido no banco). Impresso como EAN-13, a
 * maioria dos leitores recusaria.
 *
 * SEM `@page` AQUI
 * ----------------
 * A geometria da folha vem de `imprimirEtiquetas`, que já declara
 * `@page { size: 100mm 50mm; margin: 0 }` e a classe `.etiqueta`. Tudo abaixo
 * é posicionado por `style` inline de propósito: o documento de impressão não
 * clona folha de estilo nenhuma da aplicação, então classe do Tailwind aqui
 * dentro sairia sem efeito — foi assim que a etiqueta de separação já saiu em
 * branco uma vez.
 *
 * E `box-sizing` é declarado em CADA bloco que tem padding ou borda: a regra
 * do documento de impressão só alcança `.etiqueta`, não é herdada. Sem isso a
 * caixa da quantidade media 27,5 mm em vez dos 22 mm pedidos.
 */

export interface EtiquetaAlocacao {
  id: number;
  lote: number;
  recebimento: number;
  produto: number;
  descricao: string | null;
  ean: string | null;
  quantidade: number;
  sequencia: number;
  codigoBarras: string;
  alocada: boolean;
  /** Onde foi guardada, quando e por quem — só quando `alocada`. */
  localizador: number | null;
  endereco: string | null;
  alocadaEm: string | null;
  alocadaPor: string | null;
  reimpressoes: number;
}

/**
 * A GEOMETRIA DO CÓDIGO DE BARRAS.
 *
 * Os 13 dígitos em Code 128-B dão 178 módulos (start + 13 símbolos + checksum,
 * a 11 módulos cada, mais os 13 do stop). Duas restrições mandam na largura:
 *
 *  - **Zona clara**: o Code 128 exige 10 módulos de branco de cada lado. Sem
 *    ela o leitor não acha o início do código, e o sintoma é o pior possível —
 *    lê no teste da bancada e falha na prateleira, em ângulo.
 *  - **O passo da impressora**: numa térmica de 203 dpi o ponto mede 0,125 mm.
 *    Se o módulo não for múltiplo inteiro do ponto, o driver arredonda barra a
 *    barra e a mesma largura nominal sai ora com 4, ora com 5 pontos. É a causa
 *    clássica do código que "às vezes não lê".
 *
 * Módulo = 3 pontos = **0,375 mm** exatos a 203 dpi (a resolução das térmicas
 * do CD). Daí 178 × 0,375 = 66,75 mm de código, o que deixa 13,6 mm de zona
 * clara de cada lado nos 94 mm úteis — muito acima dos 3,75 mm mínimos.
 *
 * A versão anterior usava 88,19 mm de código: módulo de 0,495 mm (3,96 pontos,
 * o pior caso de arredondamento) e apenas 0,95 mm de folga sobre a zona clara
 * mínima — menos que a tolerância de corte de uma bobina.
 */
const BARRAS_LARGURA_PT = 189.2; // 66,75 mm
/*
 * 38 pt = 13,4 mm. Não é o máximo possível, é o que o ORÇAMENTO VERTICAL
 * comporta: 49,9 mm menos topo, descrição de duas linhas, número legível e
 * rodapé. A primeira tentativa usou 18 mm e a soma deu 55 mm — o rodapé saía
 * cortado pelo `overflow: hidden` da caixa, e só apareceu ao renderizar.
 */
const BARRAS_ALTURA_PT = 38;

/**
 * Corta só o que nunca caberia.
 *
 * O corte fino é do CSS (`line-clamp` de 2 linhas), que sabe a largura real;
 * contar caracteres não sabe. A versão anterior cortava em 62 caracteres numa
 * caixa que comporta ~140, e a descrição real perdia o final — justamente onde
 * mora o que distingue o SKU ("… com Acendimento Automático **Inox Bivolt**").
 * Este teto de 160 existe só para não jogar um parágrafo inteiro no DOM.
 */
function encurtar(texto: string | null, limite = 160): string {
  const t = (texto ?? '').trim();
  if (t.length <= limite) return t;
  const corte = t.lastIndexOf(' ', limite);
  return `${t.slice(0, corte > limite - 24 ? corte : limite)}…`;
}

/** Quantidade sem ".00" e com vírgula, se algum dia vier fracionada. */
function quantidadeBr(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
}

/**
 * Fonte dos números (código legível e EAN): Arial em negrito, nunca Courier.
 *
 * O traço do Courier New tem ~0,1 mm, menos que um ponto da térmica de 203 dpi
 * (0,125 mm): o driver arredonda metade dos traços para zero e o EAN saía
 * quase apagado mesmo com a obscuridade no máximo (27/10/2026, etiqueta
 * laranja e azul). Pelo mesmo motivo nada aqui é cinza (#333, #444): na
 * térmica cinza vira pontilhado, não tom.
 */
const TINTA_CHEIA = 'Arial, Helvetica, sans-serif';

export function EtiquetaAlocacaoPapel({
  etiqueta,
  total,
}: {
  etiqueta: EtiquetaAlocacao;
  /** Quantas etiquetas o produto tem — vira o "7 / 60". */
  total?: number | undefined;
}): JSX.Element {
  return (
    <div
      className="etiqueta"
      style={{
        position: 'relative',
        width: '100mm',
        height: '49.9mm',
        overflow: 'hidden',
        background: '#fff',
        color: '#000',
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxSizing: 'border-box',
        padding: '2mm 3mm',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ---------------------------------------------------------- topo --- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          gap: '2mm',
          flexShrink: 0,
          borderBottom: '0.4mm solid #000',
          paddingBottom: '1.2mm',
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '6.5pt', letterSpacing: '0.1em', fontWeight: 700 }}>ALOCAÇÃO</div>
          <div style={{ fontSize: '11pt', fontWeight: 700, lineHeight: 1.05 }}>
            {etiqueta.produto}
          </div>
        </div>

        {/*
          A sequência ocupa o vão central — que antes eram 50 mm de branco — e é
          grande porque resolve um problema físico: achar UMA etiqueta no meio
          de sessenta iguais, quando o maço cai ou sai fora de ordem.
        */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: '6.5pt', letterSpacing: '0.1em', fontWeight: 700 }}>
            ETIQUETA
          </div>
          <div style={{ fontSize: '13pt', fontWeight: 700, lineHeight: 1.05 }}>
            {etiqueta.sequencia}
            {total ? <span style={{ fontSize: '9pt', fontWeight: 400 }}> / {total}</span> : null}
          </div>
        </div>

        {/*
          A quantidade é a maior tipografia da etiqueta, e de propósito: é o
          único dado que o conferente valida contra o mundo físico antes de
          bipar. Antes o código do produto (14 pt, 5 dígitos) tinha mais massa
          de tinta que ela, e o olho batia no lugar errado.
        */}
        <div
          style={{
            boxSizing: 'border-box',
            textAlign: 'center',
            border: '0.5mm solid #000',
            borderRadius: '1.2mm',
            padding: '0.3mm 1.5mm',
            width: '24mm',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: '5.5pt', letterSpacing: '0.12em', fontWeight: 700 }}>QTDE</div>
          <div style={{ fontSize: '20pt', fontWeight: 800, lineHeight: 1 }}>
            {quantidadeBr(etiqueta.quantidade)}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------ descrição --- */}
      <div
        style={{
          fontSize: '8pt',
          lineHeight: 1.22,
          marginTop: '1.2mm',
          /*
            `flexShrink: 0` + altura para DUAS linhas.

            Sem o `flexShrink`, este bloco era esmagado pelo flex do pai e o
            `line-clamp` de 2 linhas mostrava uma linha e meia — a segunda saía
            cortada na horizontal. `minHeight` (e não `height`) para que, com
            uma linha só, a sobra vá para a barra em vez de virar faixa branca.
          */
          flexShrink: 0,
          minHeight: '7mm',
          /* Duas linhas de verdade, medidas pelo CSS na largura real. */
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          /* Código de fabricante sem espaço estourava 29 mm para fora da caixa
             e era cortado pelo `overflow`, sem reticências e sem aviso. */
          overflowWrap: 'anywhere',
        }}
      >
        {encurtar(etiqueta.descricao) || '—'}
      </div>

      {/* -------------------------------------------------------- barcode --- */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: '1mm',
        }}
      >
        <CodigoBarras
          texto={etiqueta.codigoBarras}
          larguraPt={BARRAS_LARGURA_PT}
          alturaPt={BARRAS_ALTURA_PT}
        />
        <div
          style={{
            fontFamily: TINTA_CHEIA,
            fontSize: '9.5pt',
            fontWeight: 700,
            /* `paddingLeft` compensa o espaçamento que sobra DEPOIS do último
               dígito e deslocava o número para a esquerda do eixo da barra. */
            letterSpacing: '0.16em',
            paddingLeft: '0.16em',
            marginTop: '0.8mm',
          }}
        >
          {etiqueta.codigoBarras}
        </div>
      </div>

      {/* --------------------------------------------------------- rodapé --- */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: '2mm',
          borderTop: '0.3mm solid #000',
          marginTop: '0.6mm',
          paddingTop: '0.4mm',
          /* O EAN maior não pode empurrar a altura da linha: a sobra sairia
             da barra, que é o que o coletor lê. */
          lineHeight: 1,
          fontSize: '7.5pt',
          fontWeight: 700,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        <span>
          Receb. <strong>{etiqueta.recebimento}</strong>
        </span>
        {/* O lote identifica o maço inteiro — é por ele que se reconcilia uma
            etiqueta solta com a impressão de onde ela saiu. */}
        <span>Lote {etiqueta.lote}</span>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            fontFamily: TINTA_CHEIA,
            fontSize: '10.5pt',
            letterSpacing: '0.02em',
          }}
        >
          {etiqueta.ean ?? ''}
        </span>
      </div>
    </div>
  );
}
